import React, { useEffect, useState } from "react";
import StaffLayout from "@/components/StaffLayout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  CreditCard, TrendingUp, Users, Ban, Check, X, RotateCcw, Search, Pencil,
} from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

function fmtMxn(n) {
  if (n === null || n === undefined) return "—";
  return "$" + Number(n).toLocaleString("es-MX", { maximumFractionDigits: 0 });
}
function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("es-MX", { day: "numeric", month: "short", year: "numeric" });
  } catch { return iso; }
}

const STATUS_STYLES = {
  active: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  trialing: "bg-brand-orange-500/10 text-brand-orange-300 border-primary/30",
  past_due: "bg-amber-500/10 text-amber-300 border-amber-500/30",
  canceled: "bg-gray-500/10 text-gray-300 border-gray-500/30",
  expired: "bg-red-500/10 text-red-300 border-red-500/30",
};

export default function SuperadminSuscripciones() {
  const { user } = useAuth();
  const superadmin = user?.roles?.includes("superadmin_dcc");
  const [tab, setTab] = useState("plans");
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/admin/subscriptions/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <StaffLayout user={user} superadmin={superadmin}>
      <div>
        <p className="text-xs uppercase tracking-widest text-[#93C5FD] font-semibold">
          Suscripciones
        </p>
        <h1 className="mt-2 text-3xl font-bold text-white font-display">
          All-Access DCC
        </h1>
        <p className="mt-2 text-gray-400">
          Gestiona planes y suscripciones activas. Los cobros están{" "}
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-500/10 border border-amber-500/40 text-amber-300 text-[10px] font-bold uppercase tracking-widest">
            MOCK
          </span>
        </p>

        {stats && (
          <div className="mt-8 grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="subs-stats">
            <StatTile label="Activas" value={stats.active} icon={Users} accent="text-emerald-300" />
            <StatTile label="MRR" value={fmtMxn(stats.mrr_mxn)} icon={TrendingUp} accent="text-white" />
            <StatTile label="ARR" value={fmtMxn(stats.arr_mxn)} icon={TrendingUp} accent="text-white" />
            <StatTile label="Past due" value={stats.past_due} icon={CreditCard} accent="text-amber-300" />
            <StatTile label="Canceladas" value={stats.canceled} icon={Ban} accent="text-gray-300" />
            <StatTile label="Expiradas" value={stats.expired} icon={Ban} accent="text-red-300" />
          </div>
        )}

        <div className="mt-8 border-b border-gray-800 flex gap-1" data-testid="subs-tabs">
          <TabBtn active={tab === "plans"} onClick={() => setTab("plans")} testid="tab-plans">
            Planes
          </TabBtn>
          <TabBtn active={tab === "subs"} onClick={() => setTab("subs")} testid="tab-subs">
            Suscripciones activas
          </TabBtn>
        </div>

        <div className="mt-6">
          {tab === "plans" ? <PlansTab /> : <SubsTab />}
        </div>
      </div>
    </StaffLayout>
  );
}

function StatTile({ label, value, icon: Icon, accent }) {
  return (
    <div className="p-3 bg-[#111827] border border-gray-800 rounded-lg">
      <p className="text-[10px] uppercase tracking-widest text-gray-500 flex items-center gap-1">
        <Icon className="w-3 h-3" /> {label}
      </p>
      <p className={`mt-1 font-mono text-xl font-bold ${accent}`}>{value}</p>
    </div>
  );
}

function TabBtn({ active, onClick, testid, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${
        active ? "border-[#3B82F6] text-white" : "border-transparent text-gray-400 hover:text-white"
      }`}
    >
      {children}
    </button>
  );
}

// ------------------------- Plans Tab -------------------------
function PlansTab() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/subscription-plans?status=all")
      .then((r) => setPlans(r.data.items || []))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const togglePlan = async (plan) => {
    try {
      await api.patch(`/admin/subscription-plans/${plan.slug}`, { is_active: !plan.is_active });
      toast.success(plan.is_active ? "Plan desactivado" : "Plan activado");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const save = async (updates) => {
    setSaving(true);
    try {
      await api.patch(`/admin/subscription-plans/${editing.slug}`, updates);
      toast.success("Plan actualizado");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="grid md:grid-cols-2 gap-4">
        {[0, 1].map((i) => (
          <div key={i} className="h-64 bg-[#111827] border border-gray-800 rounded-xl animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <>
      <div className="grid md:grid-cols-2 gap-4" data-testid="admin-plans-grid">
        {plans.map((p) => (
          <div
            key={p.id}
            className={`p-5 bg-[#111827] border rounded-xl ${
              p.is_active ? "border-gray-800" : "border-red-500/30"
            }`}
            data-testid={`admin-plan-${p.slug}`}
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-gray-500">
                  {p.billing_cycle}
                </p>
                <h3 className="mt-1 text-lg font-bold text-white">{p.name}</h3>
                <p className="mt-1 font-mono text-2xl text-white">{fmtMxn(p.price_mxn)}</p>
              </div>
              <span
                className={`text-[10px] px-2 py-0.5 rounded uppercase font-semibold border ${
                  p.is_active
                    ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30"
                    : "bg-red-500/10 text-red-300 border-red-500/30"
                }`}
              >
                {p.is_active ? "Activo" : "Inactivo"}
              </span>
            </div>
            <p className="mt-3 text-xs text-gray-400 line-clamp-2">{p.description}</p>
            <p className="mt-2 text-[11px] text-gray-500 font-mono">
              slug: <span className="text-white">{p.slug}</span> · orden: {p.sort_order}
            </p>
            <div className="mt-4 flex gap-2">
              <button
                type="button"
                onClick={() => setEditing(p)}
                data-testid={`admin-plan-edit-${p.slug}`}
                className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-md border border-gray-700 text-gray-200 hover:bg-white/5 text-xs font-semibold"
              >
                <Pencil className="w-3 h-3" /> Editar
              </button>
              <button
                type="button"
                onClick={() => togglePlan(p)}
                data-testid={`admin-plan-toggle-${p.slug}`}
                className={`flex-1 inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold ${
                  p.is_active
                    ? "bg-red-500/20 text-red-200 border border-red-500/40 hover:bg-red-500/30"
                    : "bg-emerald-500/20 text-emerald-200 border border-emerald-500/40 hover:bg-emerald-500/30"
                }`}
              >
                {p.is_active ? (<><X className="w-3 h-3" /> Desactivar</>) : (<><Check className="w-3 h-3" /> Activar</>)}
              </button>
            </div>
          </div>
        ))}
      </div>
      {editing && (
        <PlanEditor
          plan={editing}
          onClose={() => setEditing(null)}
          onSave={save}
          saving={saving}
        />
      )}
    </>
  );
}

function PlanEditor({ plan, onClose, onSave, saving }) {
  const [name, setName] = useState(plan.name);
  const [price, setPrice] = useState(plan.price_mxn);
  const [description, setDescription] = useState(plan.description || "");
  const [benefits, setBenefits] = useState((plan.benefits || []).join("\n"));
  const [sort, setSort] = useState(plan.sort_order || 0);

  const submit = (e) => {
    e.preventDefault();
    onSave({
      name,
      price_mxn: Number(price),
      description,
      benefits: benefits.split("\n").map((s) => s.trim()).filter(Boolean),
      sort_order: Number(sort),
    });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        data-testid="plan-editor-form"
        className="w-full max-w-lg bg-[#111827] border border-gray-800 rounded-xl p-6 space-y-4"
      >
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs uppercase tracking-widest text-[#93C5FD]">Editar plan</p>
            <h3 className="text-xl font-semibold text-white mt-1">{plan.slug}</h3>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        <EditField label="Nombre" value={name} onChange={setName} testid="plan-name" />
        <EditField label="Precio MXN" type="number" value={price} onChange={setPrice} testid="plan-price" />
        <EditField label="Orden" type="number" value={sort} onChange={setSort} testid="plan-sort" />
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-widest text-gray-400">
            Descripción
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            data-testid="plan-description"
            className="w-full mt-1 bg-[#0F172A] border border-gray-800 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-[#3B82F6]"
          />
        </div>
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-widest text-gray-400">
            Beneficios (uno por línea)
          </label>
          <textarea
            value={benefits}
            onChange={(e) => setBenefits(e.target.value)}
            rows={5}
            data-testid="plan-benefits"
            className="w-full mt-1 bg-[#0F172A] border border-gray-800 rounded-md px-3 py-2 text-sm text-white font-mono focus:outline-none focus:border-[#3B82F6]"
          />
        </div>
        <button
          type="submit"
          disabled={saving}
          data-testid="plan-editor-submit"
          className="w-full py-3 rounded-md bg-[#B84A0C] hover:bg-primary text-white font-semibold disabled:opacity-60"
        >
          {saving ? "Guardando…" : "Guardar cambios"}
        </button>
      </form>
    </div>
  );
}

function EditField({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="text-[11px] font-semibold uppercase tracking-widest text-gray-400">
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full mt-1 bg-[#0F172A] border border-gray-800 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-[#3B82F6]"
      />
    </div>
  );
}

// ------------------------- Subs Tab -------------------------
function SubsTab() {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [refunding, setRefunding] = useState(null);

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (status !== "all") params.set("status", status);
    if (q.trim()) params.set("q", q.trim());
    params.set("limit", "50");
    api.get(`/admin/subscriptions?${params.toString()}`)
      .then((r) => setSubs(r.data.items || []))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const refund = async (sub) => {
    setRefunding(sub.id);
    try {
      await api.post(`/admin/subscriptions/${sub.id}/refund-mock`);
      toast.success("Suscripción cancelada y reembolso registrado (MOCK)");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setRefunding(null);
    }
  };

  return (
    <>
      <div className="flex flex-wrap items-end gap-3 mb-4" data-testid="subs-filters">
        <div className="flex-1 min-w-64">
          <label className="text-[11px] font-semibold uppercase tracking-widest text-gray-400">
            Buscar por email/nombre
          </label>
          <div className="relative mt-1">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-500" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") load(); }}
              placeholder="alumno@…"
              data-testid="subs-search-input"
              className="w-full bg-[#0F172A] border border-gray-800 rounded-md pl-9 pr-3 py-2 text-sm text-white focus:outline-none focus:border-[#3B82F6]"
            />
          </div>
        </div>
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-widest text-gray-400">
            Estado
          </label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            data-testid="subs-status-select"
            className="mt-1 bg-[#0F172A] border border-gray-800 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-[#3B82F6]"
          >
            <option value="all">Todas</option>
            <option value="active">Activas</option>
            <option value="past_due">Past due</option>
            <option value="canceled">Canceladas</option>
            <option value="expired">Expiradas</option>
          </select>
        </div>
        <button
          type="button"
          onClick={load}
          data-testid="subs-refresh-btn"
          className="px-4 py-2 rounded-md border border-gray-700 text-gray-200 hover:bg-white/5 text-sm font-semibold"
        >
          Buscar
        </button>
      </div>

      <div className="overflow-x-auto border border-gray-800 rounded-xl" data-testid="admin-subs-table">
        <table className="w-full text-sm">
          <thead className="bg-[#0F172A] text-xs uppercase tracking-widest text-gray-400">
            <tr>
              <th className="text-left px-3 py-2">Usuario</th>
              <th className="text-left px-3 py-2">Plan</th>
              <th className="text-left px-3 py-2">Estado</th>
              <th className="text-left px-3 py-2">Inició</th>
              <th className="text-left px-3 py-2">Vence</th>
              <th className="text-right px-3 py-2">Monto</th>
              <th className="text-right px-3 py-2">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="7" className="px-4 py-8 text-center text-gray-500">Cargando…</td></tr>
            ) : subs.length === 0 ? (
              <tr><td colSpan="7" className="px-4 py-8 text-center text-gray-500">Sin suscripciones.</td></tr>
            ) : subs.map((s) => (
              <tr key={s.id} className="border-t border-gray-800" data-testid={`admin-sub-row-${s.id}`}>
                <td className="px-3 py-3">
                  <p className="text-white font-medium">{s.user_name || "—"}</p>
                  <p className="text-[11px] text-gray-500 font-mono">{s.user_email}</p>
                </td>
                <td className="px-3 py-3 text-gray-300 capitalize">
                  {(s.plan_slug || "").replace(/-/g, " ")}
                  <p className="text-[10px] text-gray-500">{s.billing_cycle}</p>
                </td>
                <td className="px-3 py-3">
                  <span className={`text-[10px] px-2 py-0.5 rounded uppercase font-semibold border ${STATUS_STYLES[s.status] || "bg-gray-500/10 text-gray-300 border-gray-500/30"}`}>
                    {s.status}
                  </span>
                  {s.cancel_at_period_end && (
                    <p className="mt-1 text-[10px] text-amber-300">cancelará al fin de periodo</p>
                  )}
                </td>
                <td className="px-3 py-3 text-gray-400 font-mono text-xs">{fmtDate(s.started_at)}</td>
                <td className="px-3 py-3 text-gray-400 font-mono text-xs">{fmtDate(s.current_period_end)}</td>
                <td className="px-3 py-3 text-right font-mono text-white">{fmtMxn(s.price_mxn)}</td>
                <td className="px-3 py-3 text-right">
                  {["active", "trialing", "past_due"].includes(s.status) && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button
                          type="button"
                          disabled={refunding === s.id}
                          data-testid={`refund-sub-btn-${s.id}`}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded border border-red-500/40 bg-red-500/10 text-red-200 hover:bg-red-500/20 text-xs font-semibold disabled:opacity-60"
                        >
                          <RotateCcw className="w-3 h-3" /> Reembolsar (MOCK)
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-[#111827] border-gray-800 text-white">
                        <AlertDialogHeader>
                          <AlertDialogTitle>¿Reembolsar y cancelar?</AlertDialogTitle>
                          <AlertDialogDescription className="text-gray-400">
                            Esta acción cancela la suscripción de{" "}
                            <span className="font-mono text-white">{s.user_email}</span> y{" "}
                            <strong className="text-red-300">revoca el acceso inmediatamente</strong>.
                            En modo mock no se contacta a Stripe, solo se registra{" "}
                            <span className="font-mono">refunded_at</span>. No se puede deshacer.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="bg-transparent border-gray-700 text-gray-200">
                            Cancelar
                          </AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => refund(s)}
                            data-testid={`confirm-refund-${s.id}`}
                            className="bg-red-600 hover:bg-red-700 text-white"
                          >
                            Sí, reembolsar
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
